import Vue from 'vue'
import { print } from 'graphql'
import { has, isEmpty, isObject, uniqueId, uniq } from 'lodash'

import { md5 } from 'tiny-hashes'

const SET_CACHE = 'SET_CACHE'
const CLEAR_CACHE = 'CLEAR_CACHE'

const CLEAR_QUERY_REFS = 'CLEAR_QUERY_REFS'
const SET_QUERY_REFS = 'SET_QUERY_REFS'
const ENABLE_CACHE = 'ENABLE_CACHE'
const DISABLE_CACHE = 'DISABLE_CACHE'

function isMutation(gquery) {
  return gquery.definitions[0].operation === 'mutation'
}

function recursiveCheck(object, total = []) {
  for (const value of Object.values(object)) {
    if (isObject(value)) {
      if (value.kind === 'Field' && value.selectionSet) {
        total.push(value.name.value)
      }
      recursiveCheck(value, total)
    }
  }
  return total
}

function parseGQueryToCategories(gquery) {
  return { categories: uniq(recursiveCheck(gquery)) }
}

function parseGQueryToCache(gquery, vars) {
  return {
    category: gquery.definitions[0].selectionSet.selections[0].name.value,
    // use md5 hash of string for key, collision should be neglectable
    key: md5(print(gquery)),
    variant: JSON.stringify(vars) || 'base',
  }
}

function setCache({ commit, state }, { data, onClear = () => {} }) {
  const id = uniqueId()

  const timerRef = setTimeout(() => {
    commit(CLEAR_CACHE, { ids: [id] })
  }, state.cacheMaxAge)

  commit(SET_CACHE, { id, data, timerRef, onClear })

  if (state.cacheByUpdatedAt.length > state.cacheMaxSize) {
    const ids = state.cacheByUpdatedAt.slice(0, 1)
    commit(CLEAR_CACHE, { ids })
  }
  return { id }
}

const store = {
  namespace: false,
  state: {
    enabled: true,
    cacheMaxAge: 5 * 60 * 1000, // 5 minutes
    // cacheMaxAge: 10000,
    cacheMaxSize: 999, // max size of cache
    queryCategoryRefs: {},
    queryRefs: {},
    cache: {},
    cacheByUpdatedAt: [],
  },
  mutations: {
    [ENABLE_CACHE](state) {
      state.enabled = true
    },
    [DISABLE_CACHE](state) {
      state.enabled = false
    },
    [CLEAR_CACHE](state, { ids }) {
      for (const id of ids) {
        const obj = state.cache[id]

        if (obj) {
          clearTimeout(obj.timerRef)
          obj.onClear(obj.id)
        }
        Vue.delete(state.cache, id)

        const index = state.cacheByUpdatedAt.indexOf(id)
        if (index !== -1) {
          Vue.delete(state.cacheByUpdatedAt, index)
        }
      }
    },
    [SET_CACHE](state, { id, timerRef, onClear, data }) {
      const point = {
        storage: data,
        timerRef,
        onClear,
        updatedAt: Date.now(),
        id,
      }
      Vue.set(state.cache, id, point)

      state.cacheByUpdatedAt.push(id)
    },
    [CLEAR_QUERY_REFS](state, { id, categories, key, variant }) {
      // delete reference
      if (has(state.queryRefs, [key, variant])) {
        Vue.delete(state.queryRefs[key], variant)
      }
      // delete parents if they are empty
      if (has(state.queryRefs, [key]) && isEmpty(state.queryRefs[key])) {
        Vue.delete(state.queryRefs, key)
      }
      // groups
      for (const category of categories) {
        if (has(state.queryCategoryRefs, [category])) {
          const index = state.queryCategoryRefs[category].indexOf(id)
          if (index !== -1) {
            Vue.delete(state.queryCategoryRefs[category], index)
          }
          if (state.queryCategoryRefs[category].length === 0) {
            Vue.delete(state.queryCategoryRefs, category)
          }
        }
      }
    },
    [SET_QUERY_REFS](state, { id, categories, key, variant }) {
      if (!state.queryRefs[key]) {
        Vue.set(state.queryRefs, key, {})
      }
      Vue.set(state.queryRefs[key], variant, id)

      // groups
      for (const category of categories) {
        if (!state.queryCategoryRefs[category]) {
          Vue.set(state.queryCategoryRefs, category, [])
        }
        state.queryCategoryRefs[category].push(id)
      }
    },
  },
  getters: {
    cacheUpdatedAt: (state) => (id) => {
      return state.cache[id] ? state.cache[id].updatedAt : null
    },
    queryCache: (state) => (gquery, variables, options = {}) => {
      if (!state.enabled) return {}

      const { key, variant } = parseGQueryToCache(gquery, variables)

      if (has(state.queryRefs, [key, variant])) {
        const id = state.queryRefs[key][variant]
        const cache = state.cache[id]

        if (!options.maxAge || Date.now() - options.maxAge < cache.updatedAt) {
          return { cached: cache.storage, id }
        }
      }
      return {}
    },
  },
  actions: {
    enableCache({ commit, state }, flag) {
      if (state.enabled !== flag) {
        if (flag) {
          commit(ENABLE_CACHE)
        } else {
          commit(DISABLE_CACHE)
        }
      }
    },
    clearQueryCategoryCache({ state, commit }, categories) {
      // get ids, and make shallow copy, so we don't ref the state when later clearing
      let ids = []
      for (const category of categories) {
        ids = [...ids, ...(state.queryCategoryRefs[category] || [])]
      }

      commit(CLEAR_CACHE, { ids })
    },
    setQueryCache({ commit, getters, state }, { gquery, variables, data }) {
      if (isMutation(gquery) || !state.enabled) return
      const { key, variant } = parseGQueryToCache(gquery, variables)
      const { categories } = parseGQueryToCategories(gquery, variables)

      const { id: oldid } = getters.queryCache(gquery, variables)
      if (oldid) {
        commit(CLEAR_CACHE, { ids: [oldid] })
      }
      const { id } = setCache(
        { commit, state },
        {
          data,
          onClear: (id) => {
            commit(CLEAR_QUERY_REFS, { categories, key, variant, id })
          },
        }
      )
      commit(SET_QUERY_REFS, { id, categories, key, variant })
    },
  },
}

export default store
